'use client';

import { useCommendaOSSchema } from '@commenda/api/commenda-os-dsl';
import {
  Label,
  LoadingIndicator,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@commenda/kakashi';
import { Info } from 'lucide-react';
import { useEffect, useMemo } from 'react';
import { SchemaFieldPicker } from '../../editor/SchemaFieldPicker';

export function EntitySelectEditor({
  props,
  onUpdateProps,
  onUpdateLabel,
  currentLabel,
}: {
  props: Record<string, unknown>;
  onUpdateProps: (key: string, value: unknown) => void;
  onUpdateLabel?: (label: string) => void;
  currentLabel?: string;
}) {
  const { entities, isLoading: isSchemaLoading } = useCommendaOSSchema();

  // Get listable entities from schema (exclude keyPersonRelationship - it's not a top-level choice; relationship fields are configured under Key Person)
  const listableEntities = useMemo(() => {
    if (!entities) return [];
    return Object.values(entities)
      .filter(
        (entity) =>
          entity.listConfig?.enabled && entity.name !== 'keyPersonRelationship'
      )
      .map((entity) => ({
        name: entity.name,
        label: entity.label,
        listPath: entity.listConfig?.path,
      }));
  }, [entities]);

  const selectedEntity = (props?.entity as string) || 'corporation';
  const selectedEntitySchema = entities?.[selectedEntity];

  useEffect(() => {
    if (!entities || !selectedEntity || !onUpdateLabel) return;

    const entity = entities[selectedEntity];
    if (!entity) return;

    const isDefaultLabel =
      !currentLabel ||
      currentLabel === 'Entity Select' ||
      currentLabel === 'New Entity Select';

    if (isDefaultLabel) {
      onUpdateLabel(`Customer selects ${entity.label}`);
    }
  }, [entities, selectedEntity, currentLabel, onUpdateLabel]);

  const handleEntityChange = (entityName: string) => {
    const entity = entities?.[entityName];
    onUpdateProps('entity', entityName);
    if (entity?.listConfig?.path) {
      onUpdateProps('listPath', entity.listConfig.path);
    }
    onUpdateProps('displayField', '');
    onUpdateProps('valueField', 'id');

    if (onUpdateLabel && entity) {
      onUpdateLabel(`Customer selects ${entity.label}`);
    }
  };

  return (
    <div className="space-y-4 pt-4 border-t border-border-secondary">
      <h4 className="txt-small-medium text-text-secondary">
        Entity Configuration
      </h4>

      {/* Entity Selection */}
      <div className="space-y-2">
        <Label>Entity Type</Label>
        {isSchemaLoading ? (
          <div className="flex items-center gap-2 p-2 border rounded-md">
            <LoadingIndicator size={16} />
            <span className="txt-small-regular text-text-tertiary">
              Loading entities...
            </span>
          </div>
        ) : (
          <Select value={selectedEntity} onValueChange={handleEntityChange}>
            <SelectTrigger>
              <SelectValue placeholder="Select an entity..." />
            </SelectTrigger>
            <SelectContent>
              {listableEntities.length > 0 ? (
                listableEntities.map((entity) => (
                  <SelectItem key={entity.name} value={entity.name}>
                    {entity.label}
                  </SelectItem>
                ))
              ) : (
                <div className="px-2 py-1.5 txt-small-regular text-text-tertiary">
                  No listable entities available
                </div>
              )}
            </SelectContent>
          </Select>
        )}
        <p className="txt-xs-regular text-text-tertiary">
          The type of entity to fetch and display in the selector
        </p>
      </div>

      {/* Display Field (Label) */}
      <div className="space-y-2">
        <Label>Display Field (Label)</Label>
        <SchemaFieldPicker
          entity={selectedEntity}
          value={(props?.displayField as string) || ''}
          onSelect={(path) => onUpdateProps('displayField', path)}
          placeholder="Select field for label..."
          readableOnly
        />
        <p className="txt-xs-regular text-text-tertiary">
          The field to display as the option label (e.g., legal name)
        </p>
      </div>

      {/* Value Field */}
      <div className="space-y-2">
        <Label>Value Field</Label>
        <SchemaFieldPicker
          entity={selectedEntity}
          value={(props?.valueField as string) || 'id'}
          onSelect={(path) => onUpdateProps('valueField', path)}
          placeholder="Select field for value..."
          readableOnly
        />
        <p className="txt-xs-regular text-text-tertiary">
          The field to store as the selected value (usually ID)
        </p>
      </div>

      {/* List Path (read-only, from schema) */}
      {selectedEntitySchema?.listConfig?.path && (
        <div className="p-3 bg-bg-secondary rounded-md border border-border-secondary">
          <p className="txt-xs-regular text-text-tertiary">
            <Info className="inline-block w-3 h-3 mr-1" />
            Data will be fetched using path:{' '}
            <code className="bg-bg-tertiary px-1 py-0.5 rounded">
              {selectedEntitySchema.listConfig.path}
            </code>
          </p>
        </div>
      )}
    </div>
  );
}
